import React, {Component, ErrorInfo, ReactNode} from 'react';
import {useTranslation} from 'react-i18next';

import {FailedLoadingDataComponent} from './failed-loading-data.component';

interface ErrorBoundaryProps {
  children?: ReactNode;
  // Called from the fallback's "Try again" button. Defaults to a full reload so
  // the page re-fetches its data instead of re-rendering the same broken state.
  onRetry?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

function ErrorBoundaryFallback({onRetry}: {onRetry: () => void}) {
  const {t} = useTranslation();

  return <FailedLoadingDataComponent title={t('error-boundary.title')} onClick={onRetry} />;
}

const reload = () => window.location.reload();

// Catches render-time throws below it so a single bad item (e.g. an API
// response missing an array the card reads `.length` on) shows a readable
// error with a retry instead of unmounting the whole React tree to a blank page.
export class ErrorBoundaryComponent extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {hasError: false};

  static getDerivedStateFromError(): ErrorBoundaryState {
    return {hasError: true};
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] render failed', error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorBoundaryFallback onRetry={this.props.onRetry || reload} />;
    }

    return this.props.children;
  }
}
